"use client";

import { Progress as ProgressPrimitive } from "@base-ui/react/progress";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";
import { cn } from "@/lib/utils";

const progressTrackVariants = cva("block h-1.5 w-full overflow-hidden rounded-full", {
  variants: {
    tone: { default: "bg-input", success: "bg-badge-success-background" },
  },
  defaultVariants: { tone: "default" },
});

const progressIndicatorVariants = cva("transition-all duration-500", {
  variants: { tone: { default: "bg-primary", success: "bg-success" } },
  defaultVariants: { tone: "default" },
});

export function Progress({
  className,
  children,
  ...props
}: ProgressPrimitive.Root.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Root
      className={cn("flex w-full flex-col gap-2", className)}
      data-slot="progress"
      {...props}
    >
      {children ? (
        children
      ) : (
        <ProgressTrack>
          <ProgressIndicator />
        </ProgressTrack>
      )}
    </ProgressPrimitive.Root>
  );
}

export function ProgressLabel({
  className,
  ...props
}: ProgressPrimitive.Label.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Label
      className={cn("font-medium text-sm", className)}
      data-slot="progress-label"
      {...props}
    />
  );
}

export function ProgressTrack({
  className,
  tone,
  ...props
}: ProgressPrimitive.Track.Props & VariantProps<typeof progressTrackVariants>): React.ReactElement {
  return (
    <ProgressPrimitive.Track
      className={cn(progressTrackVariants({ tone }), className)}
      data-slot="progress-track"
      {...props}
    />
  );
}

export function ProgressIndicator({
  className,
  tone,
  ...props
}: ProgressPrimitive.Indicator.Props &
  VariantProps<typeof progressIndicatorVariants>): React.ReactElement {
  return (
    <ProgressPrimitive.Indicator
      className={cn(progressIndicatorVariants({ tone }), className)}
      data-slot="progress-indicator"
      {...props}
    />
  );
}

export function ProgressValue({
  className,
  ...props
}: ProgressPrimitive.Value.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Value
      className={cn("text-sm tabular-nums", className)}
      data-slot="progress-value"
      {...props}
    />
  );
}

export { ProgressPrimitive };
